import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Loader2, Check, Sparkles, Eye } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Modulo61RegistroAccion() {
    const [accion, setAccion] = useState('');
    const [hora, setHora] = useState('');
    const [resultado, setResultado] = useState('');
    const [nivelProgreso, setNivelProgreso] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');
    const [claridad, setClaridad] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/draft/registro_accion`, { withCredentials: true });
                if (data.data) {
                    if (data.data.accion) setAccion(data.data.accion);
                    if (data.data.hora) setHora(data.data.hora);
                    if (data.data.resultado) setResultado(data.data.resultado);
                    if (data.data.nivelProgreso) setNivelProgreso(data.data.nivelProgreso);
                }
                if (data.read_only) { setReadOnly(true); setSubmittedAt(data.submitted_at); }
            } catch {} finally { setLoadingDraft(false); }
        })();
    }, []);

    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try { await axios.post(`${API_URL}/api/user/draft/save`, { module_id: 'registro_accion', data: { accion, hora, resultado, nivelProgreso } }, { withCredentials: true }); } catch {} finally { setSavingDraft(false); }
    }, [accion, hora, resultado, nivelProgreso]);

    useEffect(() => { if (loadingDraft || readOnly) return; const t = setTimeout(saveDraft, 2000); return () => clearTimeout(t); }, [accion, hora, resultado, nivelProgreso, saveDraft, loadingDraft, readOnly]);

    const canSubmit = !readOnly && (accion.trim().length > 0 && hora.trim().length > 0 && resultado.trim().length > 0 && nivelProgreso);

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) return;
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/user/modules/registro-accion/submit`, {
                accion, hora, resultado, nivel_progreso: nivelProgreso
            }, { withCredentials: true });
            setAiInsight(data.ai_insight);
            setClaridad(data.claridad);
            setSubmitted(true);
            toast.success('Registro de acción completado');
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al enviar'); }
        finally { setLoading(false); }
    };

    if (loadingDraft) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    if (submitted) return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <div className="flex-1 flex items-center justify-center p-4">
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl"><CardContent className="pt-8 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#5FAF9D] to-[#4A9A89] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"><Eye className="h-10 w-10 text-white" /></div>
                    <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-2" data-testid="registro-accion-success-title">Acción registrada</h2>
                    <p className="text-[#5FAF9D] font-medium mb-4">Tu progreso es visible y concreto</p>
                    {claridad && <div className="inline-block px-4 py-1.5 bg-[#5FAF9D]/10 text-[#5FAF9D] rounded-full text-sm font-medium mb-4">Claridad: {claridad}</div>}
                    {aiInsight && <div className="bg-[#F5F7F8] border-l-4 border-[#5FAF9D] p-4 rounded-r-xl text-left mb-6"><div className="flex items-start gap-3"><Sparkles className="h-5 w-5 text-[#5FAF9D] shrink-0 mt-0.5" /><p className="text-[#1F2A30] italic text-sm">"{aiInsight}"</p></div></div>}
                    <div className="mb-6"><ModuleFeedback moduleId="registro_accion" chapter={6} /></div>
                    <Link to="/usuario"><Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl" data-testid="continue-after-registro-accion">Continuar</Button></Link>
                </CardContent></Card>
            </div><SENDAFooter /></div>
    );

    return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 sm:gap-4">
                            <Link to="/usuario"><Button variant="ghost" size="icon" className="text-[#3E5F6E]"><ArrowLeft className="h-5 w-5" /></Button></Link>
                            <div>
                                <div className="flex items-center gap-2"><span className="text-xs font-semibold text-[#5FAF9D] uppercase tracking-wide">Cap. 6</span><span className="text-xs text-[#7A8A93]">6.1</span></div>
                                <h1 className="text-base sm:text-lg font-semibold text-[#1F2A30] font-['Outfit']">Registro de acción visible</h1>
                            </div>
                        </div>
                        {savingDraft && <Loader2 className="h-3 w-3 animate-spin text-[#7A8A93]" />}
                    </div>
                </div>
            </header>
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 flex-1">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <Card className="bg-white border border-[#DCE3E8] rounded-2xl sm:rounded-3xl shadow-sm overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-[#5FAF9D] to-[#4A9A89] p-4 sm:p-6 text-white">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center"><Eye className="h-5 w-5 sm:h-6 sm:w-6 text-white" /></div>
                            <div><CardTitle className="text-white text-base sm:text-lg">Registra tu acción concreta</CardTitle><p className="text-xs sm:text-sm text-white/70">Captura evidencia visible de ejecución real</p></div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-4 sm:p-6 space-y-5">
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-[#3E5F6E]">¿Qué acción realizaste?</Label>
                            <p className="text-xs text-[#7A8A93]">Describe algo concreto y observable</p>
                            <Textarea value={accion} onChange={e => setAccion(e.target.value)} placeholder="Ej: Escribí el primer párrafo del informe trimestral..." className="border-[#DCE3E8] rounded-xl min-h-[70px]" data-testid="input-accion" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-[#3E5F6E]">¿A qué hora lo hiciste?</Label>
                            <Input value={hora} onChange={e => setHora(e.target.value)} placeholder="Ej: 9:15 AM" className="border-[#DCE3E8] rounded-xl" data-testid="input-hora" />
                        </div>
                        <div className="space-y-2 p-4 bg-[#5FAF9D]/5 rounded-xl border border-[#5FAF9D]/20">
                            <Label className="text-sm font-semibold text-[#5FAF9D]">Resultado visible</Label>
                            <p className="text-xs text-[#7A8A93]">¿Qué evidencia concreta quedó?</p>
                            <Textarea value={resultado} onChange={e => setResultado(e.target.value)} placeholder="Ej: Un párrafo de 120 palabras guardado en el documento..." className="border-[#5FAF9D]/20 bg-white rounded-xl min-h-[70px]" data-testid="input-resultado" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-[#3E5F6E]">Nivel de progreso</Label>
                            <Select defaultValue={nivelProgreso || undefined} onValueChange={setNivelProgreso}>
                                <SelectTrigger className="border-[#DCE3E8] rounded-xl" data-testid="select-nivel-progreso"><SelectValue placeholder="Selecciona un nivel" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="inicio">Inicio: primer paso dado</SelectItem>
                                    <SelectItem value="avance">Avance: progreso parcial</SelectItem>
                                    <SelectItem value="completo">Completo: tarea finalizada</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex justify-end pt-4 border-t border-[#DCE3E8]">
                            <Button onClick={handleSubmit} disabled={!canSubmit || loading} className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-6 sm:px-8 py-3 rounded-xl shadow-md" data-testid="submit-registro-accion">
                                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Procesando...</> : <><Check className="h-4 w-4 mr-2" />Registrar acción</>}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </main>
            <SENDAFooter />
        </div>
    );
}
